/**
 * Live lesson room — coach side. Watches the student's pitch trace in real
 * time (relayed over the lesson WebSocket) and can push any pattern exercise
 * into the student's player mid-lesson; while the student runs it, the trace
 * is drawn against the exercise's target timeline, exactly like the student
 * sees it. Audio/video runs on the built-in WebRTC call.
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  midiToName,
  renderCallResponse,
  renderPattern,
  type ExercisePattern,
  type RenderedExercise,
  type TracePoint,
} from '@solfo/shared';
import { api } from '../../lib/api.ts';
import { openLiveSocket, sendLive, type LiveMessage } from '../../lib/live.ts';
import { isRtcSignal, useVideoCall } from '../../lib/rtc.ts';
import { exName as exLabel } from '../../lib/i18n.ts';
import { Button, Card, ErrorText, Select, Spinner, Tag, cls, fmtDateTime } from '../../lib/ui.tsx';
import LiveTraceCanvas from '../../components/LiveTraceCanvas.tsx';
import PitchCanvas from '../../components/PitchCanvas.tsx';
import VideoCallPanel from '../../components/VideoCallPanel.tsx';

interface Lesson {
  id: string;
  startsAt: string;
  endsAt: string;
  status: string;
  studentName?: string;
}

interface Exercise {
  id: string;
  name: string;
  kind: string;
  mode: string;
  patternJson: string | null;
  defaultTempoBpm: number;
  localizedJson?: string | null;
}

interface RunState {
  exerciseId: string;
  rendered: RenderedExercise;
  /** performance.now() when the student reported starting, ms. */
  startedAtMs: number;
  countInSec: number;
}

export default function CoachLiveLesson() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const wsRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [studentHere, setStudentHere] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // freeform trace (student singing outside an exercise run)
  const freeRef = useRef<TracePoint[]>([]);
  const [freePoints, setFreePoints] = useState<TracePoint[]>([]);
  // exercise run
  const runRef = useRef<RunState | null>(null);
  const [run, setRun] = useState<RunState | null>(null);
  const runPointsRef = useRef<TracePoint[]>([]);
  const [runPoints, setRunPoints] = useState<TracePoint[]>([]);
  const [runTime, setRunTime] = useState(0);
  const [lastScore, setLastScore] = useState<number | null>(null);
  // exercise the coach has selected & pushed (rendered locally for the run view)
  const pushedRef = useRef<{ exerciseId: string; rendered: RenderedExercise } | null>(null);
  // whether the student currently sees their pitch visualizer (coach-controlled)
  const [visualizerShown, setVisualizerShown] = useState(true);

  // bidirectional video call — coach is the impolite peer
  const call = useVideoCall({
    enabled: true,
    otherPresent: studentHere,
    polite: false,
    send: (msg) => sendLive(wsRef.current, msg),
  });
  const { handleSignal } = call;

  const { data: lesson } = useQuery({
    queryKey: ['lesson', lessonId],
    queryFn: async () => {
      const lessons = await api<Lesson[]>('/api/scheduling/lessons');
      return lessons.find((l) => l.id === lessonId) ?? null;
    },
  });

  const { data: exercises } = useQuery({
    queryKey: ['exercises', 'live'],
    queryFn: () => api<Exercise[]>('/api/exercises'),
  });
  const patternExercises = (exercises ?? []).filter((e) => e.kind === 'pattern' && e.patternJson);

  const [selectedId, setSelectedId] = useState('');
  const [rootMidi, setRootMidi] = useState(60);
  const [tempoBpm, setTempoBpm] = useState(100);
  const selectedExercise = patternExercises.find((e) => e.id === selectedId) ?? null;
  useEffect(() => {
    if (selectedExercise) setTempoBpm(selectedExercise.defaultTempoBpm);
  }, [selectedExercise?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onMessage = useCallback((msg: LiveMessage) => {
    if (isRtcSignal(msg)) {
      void handleSignal(msg);
      return;
    }
    switch (msg.type) {
      case 'presence':
        setStudentHere(msg.student);
        break;
      case 'trace': {
        const target = runRef.current ? runPointsRef : freeRef;
        target.current = [...target.current, ...msg.points].slice(-6000);
        if (runRef.current) setRunPoints(target.current);
        else setFreePoints(target.current);
        break;
      }
      case 'exercise-started': {
        const pushed = pushedRef.current;
        if (!pushed || pushed.exerciseId !== msg.exerciseId) return;
        runPointsRef.current = [];
        setRunPoints([]);
        setLastScore(null);
        const state: RunState = {
          exerciseId: msg.exerciseId,
          rendered: pushed.rendered,
          startedAtMs: performance.now(),
          countInSec: msg.countInSec,
        };
        runRef.current = state;
        setRun(state);
        break;
      }
      case 'exercise-finished':
        setLastScore(msg.score);
        runRef.current = null;
        setRun(null);
        freeRef.current = [];
        setFreePoints([]);
        break;
      default:
        break;
    }
  }, [handleSignal]);

  useEffect(() => {
    if (!lessonId) return;
    const ws = openLiveSocket(lessonId);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onmessage = (e) => {
      try {
        onMessage(JSON.parse(e.data as string) as LiveMessage);
      } catch {
        /* ignore malformed */
      }
    };
    return () => ws.close();
  }, [lessonId, onMessage]);

  // a (re)joining student needs the current view state: re-announce the
  // visualizer toggle and our mute/camera state whenever they appear
  useEffect(() => {
    if (!studentHere) return;
    sendLive(wsRef.current, { type: 'visualizer', show: visualizerShown });
    call.announceState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentHere]);

  function toggleVisualizer() {
    const next = !visualizerShown;
    setVisualizerShown(next);
    sendLive(wsRef.current, { type: 'visualizer', show: next });
  }

  // local clock for the run view (anchored at the student's start report)
  useEffect(() => {
    if (!run) return;
    let raf = 0;
    const tick = () => {
      const t = (performance.now() - run.startedAtMs) / 1000 - run.countInSec;
      setRunTime(t);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run]);

  function pushExercise() {
    if (!selectedExercise?.patternJson) return;
    setError(null);
    try {
      const pattern = JSON.parse(selectedExercise.patternJson) as ExercisePattern;
      // echo exercises run on the doubled call/response timeline — mirror it
      const rendered =
        selectedExercise.mode === 'call_response'
          ? renderCallResponse(pattern, rootMidi, tempoBpm).target
          : renderPattern(pattern, rootMidi, tempoBpm);
      pushedRef.current = { exerciseId: selectedExercise.id, rendered };
      sendLive(wsRef.current, {
        type: 'exercise',
        exerciseId: selectedExercise.id,
        rootMidi,
        tempoBpm,
      });
    } catch (err) {
      setError(err);
    }
  }

  const freeNow = freePoints.length > 0 ? freePoints[freePoints.length - 1]!.t : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-bold">🔴 Live lesson room</h1>
          {lesson && (
            <p className="text-xs text-slate-500">
              {lesson.studentName ?? 'Student'} · {fmtDateTime(lesson.startsAt)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Tag tone={connected ? 'green' : 'red'}>{connected ? 'connected' : 'offline'}</Tag>
          <Tag tone={studentHere ? 'green' : 'default'}>
            {studentHere ? 'student is here' : 'waiting for student…'}
          </Tag>
          <Link to="/coach/calendar" className="text-sm text-slate-500">← Calendar</Link>
        </div>
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card title="Video call">
          <VideoCallPanel
            call={call}
            otherLabel={lesson?.studentName ?? 'Student'}
            waitingText={
              studentHere
                ? 'Connecting video…'
                : 'The student has not joined the room yet — video starts when they do.'
            }
            extraControls={
              <Button
                variant={visualizerShown ? 'secondary' : 'primary'}
                className={cls('px-3 py-1.5 text-xs')}
                onClick={toggleVisualizer}
                title="Controls whether the student sees their own pitch display"
              >
                {visualizerShown ? '👁 Student sees pitch: on' : '🙈 Student sees pitch: off'}
              </Button>
            }
          />
        </Card>

        {run ? (
          <Card title="Exercise running — live trace against target">
            <PitchCanvas rendered={run.rendered} trace={runPoints} currentTime={runTime} mode="scroll" height={260} />
            {runTime < 0 && <p className="mt-2 text-center text-xs text-slate-500">Count-in…</p>}
          </Card>
        ) : (
          <Card
            title="Live pitch (student's mic)"
            actions={lastScore !== null ? <Tag tone="brand">last run: {lastScore}</Tag> : undefined}
          >
            {freePoints.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-400">
                {studentHere
                  ? 'Waiting for the student to sing — their live pitch appears here.'
                  : 'The student has not joined the room yet. They join from Lessons → Live room.'}
              </p>
            ) : (
              <LiveTraceCanvas points={freePoints} now={freeNow} height={260} />
            )}
          </Card>
        )}
      </div>

      <Card title="Push an exercise to the student">
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-56 flex-1">
            <Select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
              <option value="">Choose an exercise…</option>
              {patternExercises.map((e) => (
                <option key={e.id} value={e.id}>
                  {exLabel(e)}
                  {e.mode === 'call_response' ? ' (echo)' : ''}
                </option>
              ))}
            </Select>
          </div>
          <label className="text-xs text-slate-600">
            Root · {midiToName(rootMidi)}
            <div className="mt-0.5 flex items-center gap-1">
              <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => setRootMidi((r) => r - 1)}>−</Button>
              <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => setRootMidi((r) => r + 1)}>+</Button>
            </div>
          </label>
          <label className="text-xs text-slate-600">
            Tempo · {tempoBpm} BPM
            <div className="mt-0.5 flex items-center gap-1">
              <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => setTempoBpm((t) => Math.max(30, t - 5))}>−</Button>
              <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => setTempoBpm((t) => Math.min(240, t + 5))}>+</Button>
            </div>
          </label>
          <Button onClick={pushExercise} disabled={!selectedExercise || !studentHere || !!run}>
            ▶ Send to student
          </Button>
        </div>
        {!studentHere && (
          <p className="mt-2 text-xs text-slate-400">You can push exercises once the student joins.</p>
        )}
        <ErrorText error={error} />
      </Card>

      {!exercises && <Spinner />}
    </div>
  );
}
